import React, { useState, useRef } from 'react';
import { Monitor, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { apiClient } from '../utils/api';
import toast from 'react-hot-toast';

export const KioskPairApprovalPage: React.FC = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleInputChange = (index: number, value: string) => {
    // Strip any device prefix (K-, TV-, D-) and non-alphanumeric characters
    let sanitized = value.toUpperCase().replace(/^(K|TV|D)-/, '').replace(/[^A-Z0-9]/g, '');

    if (sanitized.length > 1) {
      // Paste handling - fill all inputs from position 0
      const chars = sanitized.slice(0, 6).split('');
      const newCode = ['', '', '', '', '', ''];
      chars.forEach((char, i) => {
        if (i < 6) {
          newCode[i] = char;
        }
      });
      setCode(newCode);

      // Focus last filled input or submit if complete
      const lastIndex = Math.min(index + chars.length, 5);
      if (lastIndex === 5 && newCode.every(c => c !== '')) {
        handleApprove(newCode.join(''));
      } else {
        inputRefs.current[lastIndex]?.focus();
      }
    } else {
      // Single character input
      const newCode = [...code];
      newCode[index] = sanitized;
      setCode(newCode);

      // Auto-advance to next input
      if (sanitized && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }

      // Auto-submit when all 6 characters are entered
      if (index === 5 && sanitized && newCode.every(c => c !== '')) {
        handleApprove(newCode.join(''));
      }
    }

    setError(null);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleApprove = async (pairingCode: string) => {
    if (pairingCode.length !== 6) {
      setError('Please enter a 6-character code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiClient.approveKioskPairing({ pairing_code: pairingCode });
      setSuccess(true);
      toast.success('Kiosk activated successfully!');
    } catch (err: any) {
      console.error('Approval error:', err);
      setError(err.message || 'Invalid or expired code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = () => {
    const pairingCode = code.join('');
    handleApprove(pairingCode);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-pink-900 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20">
              <Monitor className="w-20 h-20 text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">
            Activate Kiosk
          </h1>
          <p className="text-xl text-white/80">
            Enter the 6-character code from your kiosk display
          </p>
        </div>

        {/* Pairing Code Input */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
          {!success ? (
            <>
              <div className="flex justify-center gap-2 sm:gap-3 mb-8">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={loading || success}
                    autoFocus={index === 0}
                    className={`w-12 h-14 sm:w-16 sm:h-20 text-2xl sm:text-4xl font-bold text-center rounded-xl border-2
                      ${success
                        ? 'bg-green-500/20 border-green-400 text-green-300'
                        : error
                        ? 'bg-red-500/20 border-red-400 text-white'
                        : 'bg-white/5 border-white/30 text-white'
                      }
                      focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400
                      disabled:opacity-50 disabled:cursor-not-allowed
                      transition-all uppercase`}
                    style={{ caretColor: 'white' }}
                  />
                ))}
              </div>

              {/* Error Message */}
              {error && (
                <div className="flex items-center justify-center gap-2 text-red-300 mb-6 bg-red-500/20 p-4 rounded-xl border border-red-400/50">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleManualSubmit}
                disabled={loading || code.some(c => c === '')}
                className="w-full py-4 rounded-xl font-bold text-lg transition-all
                  bg-blue-500 hover:bg-blue-600 text-white
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    Activating...
                  </>
                ) : (
                  'Activate Kiosk'
                )}
              </button>
            </>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-6" />
              <p className="text-3xl text-white font-bold mb-4">Kiosk Activated!</p>
              <p className="text-xl text-white/80 mb-6">
                Your kiosk display should now be loading automatically.
              </p>
              <button
                onClick={() => {
                  setSuccess(false);
                  setCode(['', '', '', '', '', '']);
                  inputRefs.current[0]?.focus();
                }}
                className="px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
              >
                Activate Another Kiosk
              </button>
            </div>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-8 text-center">
          <p className="text-white/60 text-sm mb-2">
            Don't see a code on your kiosk?
          </p>
          <p className="text-white/60 text-sm">
            Make sure your kiosk is powered on and showing the activation screen
          </p>
        </div>
      </div>
    </div>
  );
};

export default KioskPairApprovalPage;
